import { useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Pill from "@/components/Pill";
import ConfirmDialog from "@/components/ConfirmDialog";
import ResponsiveDataView from "@/components/responsive/ResponsiveDataView";
import ResponsivePageHeader from "@/components/responsive/ResponsivePageHeader";
import { formatDateTime, useLanguage, useTranslation } from "@/i18n/LanguageContext";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import usePaginatedResource from "@/hooks/usePaginatedResource";

function inviteState(invite) {
  if (invite.revokedAt) return "revoked";
  if (invite.acceptedAt) return "accepted";
  if (new Date(invite.expiresAt) < new Date()) return "expired";
  return "pending";
}

function stateBadge(invite, t) {
  const state = inviteState(invite);
  const variant = state === "pending" ? "pending" : state === "accepted" ? "accepted" : state === "revoked" ? "revoked" : "neutral";
  return <Pill variant={variant}>{t(`common.states.${state}`)}</Pill>;
}

export default function Invites() {
  const { role } = useAuth();
  const t = useTranslation();
  const { language } = useLanguage();
  const isOwner = role === "owner";
  const { page, loading, reload, changePage } = usePaginatedResource(
    async (params, { signal }) => (await api.get("/invites", { params, signal })).data,
    { onError: (err) => toast.error(extractApiError(err)) },
  );
  const [creating, setCreating] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const create = async (values) => {
    try {
      const { data } = await api.post("/invites", values);
      setCreating(false);
      reload();
      if (data.emailDelivery?.status === "sent") {
        toast.success(t("invites.toast.emailSent", { email: data.invite.email }));
      } else {
        toast.success(t("invites.toast.createdFor", { email: data.invite.email }));
      }
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  const revoke = async (invite) => {
    try {
      await api.delete(`/invites/${invite.id}`);
      setConfirmRevoke(null);
      reload();
      toast.success(t("invites.toast.revoked"));
    } catch (err) {
      toast.error(extractApiError(err));
    }
  };

  return (
    <div data-testid="invites-page" className="space-y-6">
      <ResponsivePageHeader
        title={t("invites.title")}
        description={t("invites.description")}
        action={isOwner && (
          <Button onClick={() => setCreating(true)} className="w-full rounded-lg sm:w-auto" data-testid="invites-create-button">
            <Plus className="mr-1 h-4 w-4" /> {t("invites.newInvite")}
          </Button>
        )}
      />

      <ResponsiveDataView
        items={page.items}
        loading={loading}
        emptyState={t("invites.empty")}
        getRowKey={(i) => i.id}
        getRowTestId={(i) => `invite-row-${i.id}`}
        tableTestId="invites-table"
        emptyTestId="invites-empty"
        columns={[
          { key: "email", header: t("invites.columns.email"), cell: (i) => i.email },
          { key: "role", header: t("invites.columns.role"), cell: (i) => <Pill variant={i.role}>{t(`common.states.${i.role}`)}</Pill> },
          { key: "state", header: t("invites.columns.state"), cell: (i) => stateBadge(i, t) },
          { key: "expires", header: t("invites.columns.expires"), cell: (i) => <span className="font-mono text-[11px] text-muted-foreground">{formatDateTime(i.expiresAt, language)}</span> },
          { key: "created", header: t("invites.columns.created"), cell: (i) => <span className="font-mono text-[11px] text-muted-foreground">{formatDateTime(i.createdAt, language)}</span> },
        ]}
        mobileTitle={(i) => i.email}
        mobileBadge={(i) => stateBadge(i, t)}
        mobileFields={[
          { label: t("common.fields.role"), value: (i) => t(`common.states.${i.role}`) },
          { label: t("common.fields.expires"), value: (i) => formatDateTime(i.expiresAt, language) },
          { label: t("common.fields.created"), value: (i) => formatDateTime(i.createdAt, language), priority: "secondary" },
        ]}
        rowActions={[
          {
            key: "revoke",
            label: t("common.actions.revoke"),
            icon: X,
            destructive: true,
            onClick: setConfirmRevoke,
            hidden: (i) => !isOwner || inviteState(i) !== "pending",
            testId: (i) => `revoke-invite-${i.id}`,
          },
        ]}
        pagination={{
          total: page.total,
          limit: page.limit,
          offset: page.offset,
          onChange: changePage,
          testId: "invites-pager",
        }}
      />

      <CreateInviteDialog open={creating} onClose={() => setCreating(false)} onCreate={create} />
      <ConfirmDialog
        open={!!confirmRevoke}
        onOpenChange={(o) => !o && setConfirmRevoke(null)}
        title={t("invites.revoke.title")}
        description={t("invites.revoke.description", { email: confirmRevoke?.email })}
        confirmLabel={t("common.actions.revoke")}
        destructive
        onConfirm={() => revoke(confirmRevoke)}
        testId="invite-revoke-dialog"
      />
    </div>
  );
}

function CreateInviteDialog({ open, onClose, onCreate }) {
  const t = useTranslation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");

  useEffect(() => {
    if (open) {
      setEmail("");
      setRole("admin");
    }
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    onCreate({ email: email.trim().toLowerCase(), role });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-border" data-testid="invite-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{t("invites.create.title")}</DialogTitle>
          <DialogDescription className="text-xs">{t("invites.create.description")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs">{t("common.fields.email")}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="invite-create-email" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t("common.fields.role")}</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invite-create-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">{t("common.states.admin")}</SelectItem>
                <SelectItem value="owner">{t("common.states.owner")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-lg" data-testid="invite-create-cancel">{t("common.actions.cancel")}</Button>
            <Button type="submit" className="rounded-lg" data-testid="invite-create-submit">{t("invites.create.submit")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
